import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { CuriosityNamespaceType, useCuriosityTranslation } from '@procureai/curiosity-ui-mui';
import {
  NumberFormatPreset,
  VerticalBarChart,
  Widget,
  WidgetContent,
  WidgetHeader,
  formatNumber,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { isEmpty, isNil } from 'ramda';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import { useDateRange } from '~/components/DateRangeProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { useCommodityGroupDetail } from '~/modules/Module.hooks';
import { convertDateRangeToTimeDimension } from '~/util/cube';

const formatValue = formatNumber(NumberFormatPreset.INTEGER_COMPACT);

const CUBE = 'AverageNegotiationSavings';
export const NegotiationsSavingsCommodityGroupWidget = () => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'NegotiationsSavingsCommodityGroupWidget' });
  const { commodityGroupId } = useCommodityGroupDetail();
  const [widgetRef, active] = useScrollIntoView({ hash: 'negotiations-savings-commodity-group' });
  const { ct } = useCuriosityTranslation(CuriosityNamespaceType.COMMODITY_GROUP);
  const commodityGroupField = commodityGroupId ? `commodity_group_id` : `root_commodity_group_id`;

  const { currentDateRange } = useDateRange();

  const filters: Filter[] = commodityGroupId
    ? [
        {
          member: `${CUBE}.root_commodity_group_id`,
          operator: 'equals',
          values: [commodityGroupId],
        },
      ]
    : [];

  const query = useCubeQuery({
    measures: [`${CUBE}.average_saving`, `${CUBE}.average_saving_rate`],
    dimensions: [`${CUBE}.${commodityGroupField}`],
    timeDimensions: convertDateRangeToTimeDimension(`${CUBE}.date`, currentDateRange),
    filters: filters ?? [],
    order: { [`${CUBE}.average_saving`]: 'desc' },
    limit: 10,
  });

  const data = query.resultSet
    ?.rawData()
    ?.map((segment) => ({
      label: ct(String(segment[`${CUBE}.${commodityGroupField}`]))?.value ?? '',
      value: Number(segment[`${CUBE}.average_saving`]), // average saving amount in EUR
      valueRate: Number(segment[`${CUBE}.average_saving_rate`]), // average saving rate as a percentage
    }))
    .filter((item) => item.label !== 'null' && item.label && item.value && item.valueRate);

  const isLoading = query.isLoading;
  const isDataEmpty = isEmpty(data) || isNil(data) || data.every((entry) => entry.value === 0 && entry.valueRate === 0);

  // Tooltip content format: "Average saving: val EUR (val in %)"
  const tooltipFormatter = useCallback(
    (value: any, _name?: string): [value: string | number, label: string] => {
      // get the second value from the data object to add it to the new label
      const averageSaving = data?.find((segment) => segment.value === value)?.valueRate;

      return [
        `${formatValue(value)} EUR (${formatNumber(NumberFormatPreset.PERCENT_DECIMAL, averageSaving)})`,
        t('tooltipLabel'),
      ];
    },
    [data, t],
  );

  const xAxis = {
    type: 'number',
    tickFormatter: (v: number) => formatNumber(NumberFormatPreset.INTEGER_COMPACT, v),
  };

  const yAxis = {
    type: 'category',
    dataKey: 'label',
    width: 100,
  };

  return (
    <Widget hover="js" ref={widgetRef} active={active} data-intercom-target="NegitiationsSavingsCommodityGroupWidget">
      <WidgetHeader title={t('title')} />
      <WidgetContent>
        {!isDataEmpty ? (
          <VerticalBarChart
            tooltip
            legend
            data={data}
            xAxis={xAxis}
            yAxis={yAxis}
            tooltipFormatter={tooltipFormatter}
          />
        ) : isLoading ? (
          <CircularProgressCentered sx={{ position: 'relative' }} />
        ) : (
          <PlaceholderWidget />
        )}
      </WidgetContent>
    </Widget>
  );
};
